import Head from 'next/head';
import { useRouter } from 'next/router';

const SEOHead = ({
  title = 'Fenkparet - Boutique en ligne moderne',
  description = 'Découvrez notre collection de produits de qualité. Livraison rapide en Haïti.',
  keywords = 'boutique en ligne, Haiti, e-commerce, produits de qualité, livraison rapide',
  image = '/images/og-image.png',
  type = 'website',
  noindex = false,
  canonical,
  structuredData,
  product,
  article
}) => {
  const router = useRouter();
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.fenkparet.com';
  const currentUrl = `${siteUrl}${router.asPath}`;
  const finalCanonical = canonical || currentUrl;

  // Images may already be absolute (e.g. Cloudinary) — only prefix relative paths
  const absoluteUrl = (url) => (!url ? url : /^https?:\/\//i.test(url) ? url : `${siteUrl}${url}`);

  // Generate product structured data
  const generateProductStructuredData = () => {
    if (!product) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.images?.map(img => absoluteUrl(img.url)).filter(Boolean) || [],
      "brand": {
        "@type": "Brand",
        "name": "Fenkparet"
      },
      "offers": {
        "@type": "Offer",
        "url": currentUrl,
        "priceCurrency": "HTG",
        "price": product.price,
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "Fenkparet"
        }
      },
      "aggregateRating": product.rating?.count > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": product.rating.average,
        "reviewCount": product.rating.count
      } : undefined
    };
  };

  // Generate article structured data
  const generateArticleStructuredData = () => {
    if (!article) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "image": article.image ? absoluteUrl(article.image) : null,
      "author": {
        "@type": "Organization",
        "name": "Fenkparet"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Fenkparet",
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/images/logo.png`
        }
      },
      "datePublished": article.publishedAt,
      "dateModified": article.updatedAt || article.publishedAt
    };
  };

  // Organization structured data
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Fenkparet",
    "url": siteUrl,
    "logo": `${siteUrl}/images/logo.png`,
    "description": "Boutique en ligne moderne en Haïti offrant des produits de qualité avec livraison rapide.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "HT",
      "addressLocality": "Port-au-Prince"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+509-4745-8821",
      "contactType": "customer service",
      "email": "info@fenkparet.com"
    },
    "sameAs": [
      "https://www.facebook.com/share/1CMGWQkgvc/",
      "https://www.instagram.com/fenkparet",
      "https://x.com/FenkParet",
      "https://youtube.com/@fenkparet2862"
    ]
  };

  // Website structured data
  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Fenkparet",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/products?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const combinedStructuredData = [
    organizationStructuredData,
    websiteStructuredData,
    ...(product ? [generateProductStructuredData()] : []),
    ...(article ? [generateArticleStructuredData()] : []),
    ...(structuredData ? [structuredData] : [])
  ].filter(Boolean);

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={finalCanonical} />

      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={absoluteUrl(image)} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Fenkparet" />
      <meta property="og:locale" content="fr_HT" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteUrl(image)} />
      <meta name="twitter:site" content="@FenkParet" />

      {/* Additional Meta Tags */}
      <meta name="author" content="Fenkparet" />
      <meta name="language" content="fr" />
      <meta name="geo.region" content="HT" />
      <meta name="geo.country" content="Haiti" />

      {/* Structured Data */}
      {combinedStructuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data, null, 2)
          }}
        />
      ))}

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://us-central1-fenparet-website.cloudfunctions.net/api" />

      {/* Alternative languages */}
      <link rel="alternate" hrefLang="fr-HT" href={currentUrl} />
      <link rel="alternate" hrefLang="fr" href={currentUrl} />
    </Head>
  );
};

export default SEOHead;